import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookCollectionService, BookCollection, BookCollectionStatistics } from '../services/bookCollectionService';
import CloneCollectionDialog from '../components/CloneCollectionDialog';

const BookCollectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState<BookCollection[]>([]);
  const [collectionToClone, setCollectionToClone] = useState<BookCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<BookCollectionStatistics | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [collectionToDelete, setCollectionToDelete] = useState<BookCollection | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Fetch data from API
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch collections and statistics
      const [collectionsResponse, statsResponse] = await Promise.all([
        BookCollectionService.getCollections(),
        BookCollectionService.getStatistics()
      ]);

      // Handle collections response
      let collectionsData: BookCollection[] = [];
      if (collectionsResponse) {
        collectionsData = collectionsResponse.data || collectionsResponse.result || collectionsResponse;
        setCollections(Array.isArray(collectionsData) ? collectionsData : []);
      } else {
        console.error('Failed to fetch collections');
        setCollections([]);
      }

      // Handle statistics response
      if (statsResponse && statsResponse.data) {
        setStatistics(statsResponse.data);
      }

      setError(null);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Không thể tải dữ liệu. Vui lòng thử lại.');
      setCollections([]);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete collection
  const handleDeleteCollection = (collection: BookCollection) => {
    setCollectionToDelete(collection);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!collectionToDelete) return;

    try {
      setIsDeleting(true);
      const id = collectionToDelete.collection_id || collectionToDelete._id;
      if (!id) {
        throw new Error('Collection ID not found');
      }

      const response = await BookCollectionService.deleteCollection(id);
      
      // Check if the deletion was successful
      if (response && response.success) {
        // Remove the collection from the local state
        setCollections(prevCollections => 
          prevCollections.filter(col => 
            (col.collection_id || col._id) !== (collectionToDelete.collection_id || collectionToDelete._id)
          )
        );

        // Update statistics locally without API call
        setStatistics((prevStats) => prevStats ? ({
          ...prevStats,
          total_collections: prevStats.total_collections - 1,
          published_collections: collectionToDelete.status === 'published' 
            ? prevStats.published_collections - 1 
            : prevStats.published_collections,
          draft_collections: collectionToDelete.status === 'draft' 
            ? prevStats.draft_collections - 1 
            : prevStats.draft_collections,
        }) : null);

        setDeleteConfirmOpen(false);
        setCollectionToDelete(null);
        setError(null);
      } else {
        const errorMessage = response?.message || 'Không thể xóa bộ sách. Vui lòng thử lại.';
        setError(errorMessage);
      }
    } catch (err: any) {
      console.error('Failed to delete collection:', err);
      setError(err?.response?.data?.message || 'Không thể xóa bộ sách. Vui lòng thử lại.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCollectionToDelete(null);
  };
  
  const filteredCollections = collections.filter(collection => {
    const searchMatch = !searchQuery || 
      (collection.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (collection.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (collection.collection_id || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    return searchMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'published': 
        return 'bg-green-100 text-green-800';
      case 'draft': 
        return 'bg-yellow-100 text-yellow-800';
      default: 
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'published': return 'Đã xuất bản';
      case 'draft': return 'Nháp';
      default: return status || 'Không xác định';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý Bộ sách</h1>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-current border-t-transparent text-blue-600 rounded-full"></div>
          <p className="mt-4 text-gray-600">Đang tải danh sách bộ sách...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && collections.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý Bộ sách</h1>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <svg className="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Có lỗi xảy ra</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Tải lại trang
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Bộ sách</h1>
        </div>
        <div className="flex space-x-3">
          <Link
            to="/books/collections/new"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Tạo bộ sách mới
          </Link>
        </div>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổng bộ sách</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statistics?.total_collections ?? collections.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Đã xuất bản</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statistics?.published_collections ?? collections.filter(c => c.status === 'published').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-indigo-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Nháp</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statistics?.draft_collections ?? collections.filter(c => c.status === 'draft').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Tìm kiếm bộ sách theo tên, mô tả hoặc ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Error message if any */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      {/* Collections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCollections.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy bộ sách</h3>
            <p className="text-gray-500">Thử thay đổi bộ lọc hoặc tạo bộ sách mới.</p>
          </div>
        ) : (
          filteredCollections.map((collection) => (
            <div key={collection._id || collection.collection_id} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
              {/* Cover Image */}
              <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                {collection.cover_image_url ? (
                  <img 
                    src={collection.cover_image_url} 
                    alt={collection.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="h-20 w-20 text-white opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <Link 
                    to={`/books/collections/${collection.collection_id || collection._id}`}
                    className="text-lg font-semibold text-gray-900 hover:text-blue-600 line-clamp-2 flex-1"
                  >
                    {collection.title}
                  </Link>
                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(collection.status)}`}>
                    {getStatusDisplay(collection.status)}
                  </span>
                </div>

                {collection.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {collection.description}
                  </p>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    {collection.book_count || collection.book_ids?.length || 0} sách
                  </div>

                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/books/collections/${collection.collection_id || collection._id}`}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Xem chi tiết
                    </Link>
                    <button
                      onClick={() => setCollectionToClone(collection)}
                      className="text-gray-500 hover:text-gray-800"
                      title="Nhân bản bộ sách"
                      aria-label={`Nhân bản ${collection.title}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteCollection(collection)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {collectionToClone && (
        <CloneCollectionDialog
          open
          onOpenChange={(open) => !open && setCollectionToClone(null)}
          collection={collectionToClone}
          onSuccess={(newCollection) => navigate(`/books/collections/${newCollection.collection_id || newCollection._id}`)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Xác nhận xóa bộ sách</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn xóa bộ sách <strong>"{collectionToDelete?.title}"</strong>?
                </p>
                <p className="text-sm text-red-600 mt-2">
                  Hành động này không thể hoàn tác. (Các sách trong bộ sách sẽ không bị xóa)
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={cancelDelete}
                  disabled={isDeleting}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded disabled:opacity-50"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 flex items-center"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Đang xóa...
                    </>
                  ) : (
                    'Xóa bộ sách'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookCollectionsPage;

